/** Every keyboard shortcut the app answers to, grouped by where it works (ported from the prototype's lib/shortcuts.ts, without the
 *  the bot keys, bulk ticking (M2b) and team chat (post-launch); M4 added the draft's keys). The help sheet renders this list and nothing else, so a
 *  shortcut that is added or removed is added or removed here. */
export interface Shortcut { keys: string[]; /** a second way to do the same */ or?: string[]; does: string; }
export interface ShortcutGroup { id: string; title: string; where: string; items: Shortcut[]; }

export const SHORTCUTS: ShortcutGroup[] = [
  { id: "anywhere", title: "Anywhere", where: "On every screen", items: [
    { keys: ["?"], does: "Open this sheet" },
    { keys: ["Esc"], does: "Close a dialog, a menu or the search" },
  ] },
  { id: "list", title: "Ticket list", where: "In the inbox", items: [
    { keys: ["↓"], or: ["J"], does: "Next ticket" },
    { keys: ["↑"], or: ["K"], does: "Previous ticket" },
    { keys: ["↵"], does: "Open the ticket" },
    { keys: ["C"], does: "New ticket: write to a customer, or log a call" },
    { keys: ["Esc"], does: "Close the ticket, back to the list" },
  ] },
  { id: "ticket", title: "Ticket", where: "With a ticket open", items: [
    { keys: ["R"], does: "Reply" },
    { keys: ["D"], does: "Discuss with the team" }, // ticket discussion round two: the ticket's team discussion; D since the discussion polish round (2026-11-02, was N)
    { keys: ["T"], does: "Add a tag" },
    { keys: ["E"], does: "Resolve the ticket" },
    { keys: ["A"], does: "Ask the AI" }, // the composer-and-fixes round (2026-11-03): the reply bar's AI menu; W, I and O pick inside it
    { keys: ["W"], does: "Ask the AI for a draft, or to change its draft" }, // the queue-and-chat-column round (2026-11-03): W, was P; the spot check keeps W while its bar shows
    { keys: ["I"], does: "Investigate the code" },
    { keys: ["O"], does: "Open in Claude" },
    { keys: ["⌘", "↵"], does: "Send what you wrote, or the AI's draft as it is" },
  ] },
  { id: "ask", title: "While the AI works on an ask", where: "After W, until the draft is ready", items: [
    { keys: ["S"], does: "Stop the ask, keep the draft as it was" },
    { keys: ["K"], does: "Keep working in the background and move on" },
  ] },
  { id: "spot", title: "Spot check", where: "On a reply the AI sent on its own", items: [ // M5 round nine
    { keys: ["G"], does: "Good: the next unchecked reply opens" },
    { keys: ["W"], does: "Wrong: the ticket reopens in your queue" },
  ] },
  { id: "composer", title: "Reply box", where: "While writing a reply", items: [
    { keys: ["/"], does: "Commands and saved replies" },
    { keys: ["↑"], or: ["↓"], does: "Move through the suggestions" },
    { keys: ["↵"], or: ["Tab"], does: "Take the suggestion" },
    { keys: ["⌘", "↵"], does: "Send" },
    { keys: ["Esc"], does: "Close the composer, keep the draft" },
  ] },
];

/** "?" typed outside a text field opens the sheet. Shift is part of typing "?" on most layouts, so it is not a modifier here. */
export function opensHelp(e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">, inField: boolean): boolean {
  return e.key === "?" && !inField && !e.metaKey && !e.ctrlKey && !e.altKey;
}

/** A dialog owns the keyboard while it is open: the screen behind it answers to nothing. */
export const dialogOpen = () => typeof document !== "undefined" && !!document.querySelector('[role="dialog"]');
